import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { logger } from '../../utils/logger';
import { logClientErrorFromInnerBoundary } from '../../utils/reportClientIncident';

interface Props {
  moduleKey: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

/**
 * Isolates lazy role-module crashes so the rest of the dashboard shell stays usable
 * and client-errors include which module failed (React #130 triage).
 */
export class DashboardModuleErrorBoundary extends Component<Props, State> {
  private hasReported = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): Pick<State, 'hasError'> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    this.setState({ errorId });

    logger.error('Dashboard module render failed', error, {
      moduleKey: this.props.moduleKey,
      errorId,
      componentStack: errorInfo.componentStack?.slice(0, 500),
    });

    if (this.hasReported) {
      return;
    }
    this.hasReported = true;

    logClientErrorFromInnerBoundary(error, `DashboardModule:${this.props.moduleKey}`, {
      correlation_id: errorId,
      component_stack: errorInfo.componentStack ?? null,
    });
  }

  private handleRetry = (): void => {
    this.hasReported = false;
    this.setState({ hasError: false, errorId: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const panelLabel = String(this.props.moduleKey ?? 'unknown').replace(/_/g, ' ');

    return (
      <Box sx={{ py: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={this.handleRetry}>
              Retry
            </Button>
          }
        >
          <Typography variant="subtitle2" component="div">
            This dashboard section failed to load
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            The {panelLabel} panel hit an unexpected error. Other roles and
            navigation should still work — try another tab or retry.
          </Typography>
          {this.state.errorId ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.8 }}>
              Reference ID: {this.state.errorId}
            </Typography>
          ) : null}
        </Alert>
      </Box>
    );
  }
}

export default DashboardModuleErrorBoundary;
